import {
    MarketPolicy,
    PolicyPayload,
    RefundPolicy,
    ReplacementPolicy,
    ReturnPolicySection,
    CancellationPolicy,
} from "@/views/setup/return-policy/types";
import { DEFAULT_POLICY } from "@/views/setup/return-policy/policy-constants";

const METADATA_KEYS = ["_id", "createdAt", "updatedAt", "tenant", "isActive", "__v"];

export const clonePolicy = <T>(value: T): T => JSON.parse(JSON.stringify(value));

const stripMetaDeep = (value: any): any => {
    if (Array.isArray(value)) {
        return value.map((item) => stripMetaDeep(item));
    }
    if (value && typeof value === "object") {
        const result = {};
        Object.keys(value).forEach((key) => {
            if (!METADATA_KEYS.includes(key)) {
                result[key] = stripMetaDeep(value[key]);
            }
        });
        return result;
    }
    return value;
};

export const normalizeTags = (tags: string[]) => {
    return tags
        .map((tag) => `${tag || ""}`.trim())
        .filter((tag, index, arr) => tag.length > 0 && arr.indexOf(tag) === index);
};

export const hydratePolicy = (policy: PolicyPayload | null): PolicyPayload => {
    if (!policy) return clonePolicy(DEFAULT_POLICY);
    const incoming = stripMetaDeep(policy);
    return {
        cancellation: {
            ...DEFAULT_POLICY.cancellation,
            ...(incoming.cancellation || {}),
            fee: {
                ...DEFAULT_POLICY.cancellation.fee,
                ...((incoming.cancellation && incoming.cancellation.fee) || {}),
            },
            reasons: normalizeTags(
                (incoming.cancellation && incoming.cancellation.reasons) ||
                    DEFAULT_POLICY.cancellation.reasons,
            ),
        },
        return: {
            ...DEFAULT_POLICY.return,
            ...(incoming.return || {}),
            restockingFee: {
                ...DEFAULT_POLICY.return.restockingFee,
                ...((incoming.return && incoming.return.restockingFee) || {}),
            },
            reasons: normalizeTags(
                (incoming.return && incoming.return.reasons) || DEFAULT_POLICY.return.reasons,
            ),
            returnableItemTypes:
                (incoming.return && incoming.return.returnableItemTypes) ||
                DEFAULT_POLICY.return.returnableItemTypes,
            nonReturnableItemTypes:
                (incoming.return && incoming.return.nonReturnableItemTypes) ||
                DEFAULT_POLICY.return.nonReturnableItemTypes,
        },
        replacement: {
            ...DEFAULT_POLICY.replacement,
            ...(incoming.replacement || {}),
            reasons: normalizeTags(
                (incoming.replacement && incoming.replacement.reasons) ||
                    DEFAULT_POLICY.replacement.reasons,
            ),
        },
        refund: {
            ...DEFAULT_POLICY.refund,
            ...(incoming.refund || {}),
            deductions: {
                ...DEFAULT_POLICY.refund.deductions,
                ...((incoming.refund && incoming.refund.deductions) || {}),
            },
        },
        marketPolicies: ((incoming.marketPolicies || []) as MarketPolicy[]).map((item) =>
            sanitizeMarketPolicy(item),
        ),
    };
};

export const sanitizeMarketPolicy = (marketPolicy: MarketPolicy): MarketPolicy => {
    const clean = stripMetaDeep(marketPolicy || {}) as MarketPolicy;
    const result: MarketPolicy = {
        marketName: `${clean.marketName || ""}`.trim(),
    };
    if (clean.conditions && `${clean.conditions}`.trim()) {
        result.conditions = `${clean.conditions}`.trim();
    }
    if (clean.cancellation) {
        result.cancellation = {
            ...clean.cancellation,
            reasons: clean.cancellation.reasons
                ? normalizeTags(clean.cancellation.reasons as string[])
                : clean.cancellation.reasons,
        };
    }
    if (clean.return) {
        result.return = {
            ...clean.return,
            reasons: clean.return.reasons
                ? normalizeTags(clean.return.reasons as string[])
                : clean.return.reasons,
            returnableItemTypes: clean.return.returnableItemTypes || clean.return.returnableItemTypes,
            nonReturnableItemTypes:
                clean.return.nonReturnableItemTypes || clean.return.nonReturnableItemTypes,
        };
    }
    if (clean.replacement) {
        result.replacement = {
            ...clean.replacement,
            reasons: clean.replacement.reasons
                ? normalizeTags(clean.replacement.reasons as string[])
                : clean.replacement.reasons,
        };
    }
    if (clean.refund) {
        result.refund = {
            ...clean.refund,
            deductions: {
                ...DEFAULT_POLICY.refund.deductions,
                ...(clean.refund.deductions || {}),
            },
        };
    }
    return result;
};

export const serializePolicy = (policy: PolicyPayload) => JSON.stringify(stripMetaDeep(policy));

const isPercentageInvalid = (type: string, value: number) =>
    type === "PERCENTAGE" && (value < 0 || value > 100);

const validateCancellation = (
    section: Partial<CancellationPolicy>,
    keyPrefix: string,
    errors: Record<string, string>,
) => {
    if (!section.enabled) return;
    if ((section.windowMinutes || 0) < 0) {
        errors[`${keyPrefix}.windowMinutes`] = "Window minutes must be 0 or more.";
    }
    if (!section.reasons || section.reasons.length < 1) {
        errors[`${keyPrefix}.reasons`] = "Add at least 1 reason.";
    }
    if (!section.freeCancellationBeforeStatus) {
        errors[`${keyPrefix}.freeCancellationBeforeStatus`] = "Please choose a status.";
    }
    if (!section.allowedBeforeStatuses || section.allowedBeforeStatuses.length < 1) {
        errors[`${keyPrefix}.allowedBeforeStatuses`] = "Pick at least 1 status.";
    }
    if (section.fee) {
        if ((section.fee.value || 0) < 0) {
            errors[`${keyPrefix}.fee.value`] = "Fee must be 0 or more.";
        }
        if (isPercentageInvalid(section.fee.type || "FLAT", Number(section.fee.value || 0))) {
            errors[`${keyPrefix}.fee.value`] = "Percentage must be between 0 and 100.";
        }
    }
};

const validateReturn = (
    section: Partial<ReturnPolicySection>,
    keyPrefix: string,
    errors: Record<string, string>,
) => {
    if (!section.enabled) return;
    const days = Number(section.windowDays || 0);
    if (days < 1 || days > 365) {
        errors[`${keyPrefix}.windowDays`] = "Window days must be between 1 and 365.";
    }
    if (!section.reasons || section.reasons.length < 1) {
        errors[`${keyPrefix}.reasons`] = "Add at least 1 reason.";
    }
    if (section.requireImages && Number(section.minImages || 0) < 1) {
        errors[`${keyPrefix}.minImages`] = "Minimum images must be 1 or more.";
    }
    if (Number(section.autoApproveBelow || 0) < 0) {
        errors[`${keyPrefix}.autoApproveBelow`] = "Value must be 0 or more.";
    }
    if (section.restockingFee) {
        if (Number(section.restockingFee.value || 0) < 0) {
            errors[`${keyPrefix}.restockingFee.value`] = "Fee must be 0 or more.";
        }
        if (
            isPercentageInvalid(
                section.restockingFee.type || "FLAT",
                Number(section.restockingFee.value || 0),
            )
        ) {
            errors[`${keyPrefix}.restockingFee.value`] = "Percentage must be between 0 and 100.";
        }
    }
};

const validateReplacement = (
    section: Partial<ReplacementPolicy>,
    keyPrefix: string,
    errors: Record<string, string>,
) => {
    if (!section.enabled) return;
    if (Number(section.windowDays || 0) < 1) {
        errors[`${keyPrefix}.windowDays`] = "Window days must be 1 or more.";
    }
    if (Number(section.maxPerOrder || 0) < 1) {
        errors[`${keyPrefix}.maxPerOrder`] = "Max replacements per order must be 1 or more.";
    }
    if (!section.reasons || section.reasons.length < 1) {
        errors[`${keyPrefix}.reasons`] = "Add at least 1 reason.";
    }
};

const validateRefund = (
    section: Partial<RefundPolicy>,
    keyPrefix: string,
    errors: Record<string, string>,
) => {
    if (Number(section.processingDays || 0) < 1) {
        errors[`${keyPrefix}.processingDays`] = "Processing days must be 1 or more.";
    }
};

export const validatePolicy = (policy: PolicyPayload) => {
    const errors: Record<string, string> = {};
    validateCancellation(policy.cancellation, "cancellation", errors);
    validateReturn(policy.return, "return", errors);
    validateReplacement(policy.replacement, "replacement", errors);
    validateRefund(policy.refund, "refund", errors);

    const marketNameMap: Record<string, number> = {};
    policy.marketPolicies.forEach((market, index) => {
        const name = `${market.marketName || ""}`.trim().toLowerCase();
        if (!name) {
            errors[`marketPolicies.${index}.marketName`] = "Market name is required.";
            return;
        }
        marketNameMap[name] = (marketNameMap[name] || 0) + 1;
    });
    Object.keys(marketNameMap).forEach((name) => {
        if (marketNameMap[name] > 1) {
            policy.marketPolicies.forEach((market, index) => {
                if (`${market.marketName || ""}`.trim().toLowerCase() === name) {
                    errors[`marketPolicies.${index}.marketName`] = "Market name must be unique.";
                }
            });
        }
    });

    policy.marketPolicies.forEach((market, index) => {
        if (market.cancellation) {
            validateCancellation(
                { ...DEFAULT_POLICY.cancellation, ...market.cancellation },
                `marketPolicies.${index}.cancellation`,
                errors,
            );
        }
        if (market.return) {
            validateReturn(
                { ...DEFAULT_POLICY.return, ...market.return },
                `marketPolicies.${index}.return`,
                errors,
            );
        }
        if (market.replacement) {
            validateReplacement(
                { ...DEFAULT_POLICY.replacement, ...market.replacement },
                `marketPolicies.${index}.replacement`,
                errors,
            );
        }
        if (market.refund) {
            validateRefund(
                { ...DEFAULT_POLICY.refund, ...market.refund },
                `marketPolicies.${index}.refund`,
                errors,
            );
        }
    });

    return {
        isValid: Object.keys(errors).length === 0,
        errors,
    };
};

export const toApiPayload = (policy: PolicyPayload): PolicyPayload => {
    const clean = hydratePolicy(stripMetaDeep(policy));
    clean.marketPolicies = clean.marketPolicies.map((market) => sanitizeMarketPolicy(market));
    return clean;
};

export const formatFeeText = (fee?: { type?: string; value?: number }) => {
    if (!fee || Number(fee.value || 0) === 0) return "No fee";
    if (fee.type === "PERCENTAGE") return `${fee.value}%`;
    return `${fee.value} flat`;
};

export const marketSummaryLines = (market: MarketPolicy) => {
    const cancelLine = market.cancellation
        ? market.cancellation.enabled === false
            ? "Cancel: Disabled"
            : `Cancel: ${market.cancellation.windowMinutes || 0} min window`
        : "Cancel: Inherits base policy";

    const returnLine = market.return
        ? market.return.enabled === false
            ? "Return: Disabled"
            : `Return: ${market.return.windowDays || 0} days, ${formatFeeText(
                  market.return.restockingFee,
              )} restocking fee`
        : "Return: Inherits base policy";

    const replacementLine = market.replacement
        ? market.replacement.enabled === false
            ? "Replacement: Disabled"
            : `Replacement: ${market.replacement.windowDays || 0} days`
        : "Replacement: Inherits base policy";

    const refundLine = market.refund
        ? `Refund: ${market.refund.method || "ORIGINAL_PAYMENT"}`
        : "Refund: Inherits base policy";

    const conditions = market.conditions
        ? `Conditions: ${market.conditions}`
        : "Conditions: Not specified";

    return [returnLine, replacementLine, cancelLine, refundLine, conditions];
};
